// Safety, Briefings & Job Planning — deterministic template builders + checklists.
// Single bundled module to keep additive surface area small.
import { polishProse, MISSING } from "@/lib/ddr/templates";

const orMissing = (v) => (v && String(v).trim() ? String(v).trim() : MISSING);
const polishedOrMissing = (v) => polishProse(v) || MISSING;

// ====== Toolbox Talk =======================================================
export function buildToolboxTalk(state) {
  const h = state.header || {};
  const t = state.topic || {};
  const lines = [];
  const push = (s) => lines.push(s);
  const sec = (l) => lines.push(`\n=== ${l.toUpperCase()} ===\n`);
  sec("Toolbox Talk Title");
  push(`${orMissing(t.topic)} — ${orMissing(h.reportDate)}`);
  sec("Purpose");
  push(`Toolbox talk delivered to the crew prior to work activities on ${orMissing(h.rigName)} (${orMissing(h.wellName)}).`);
  sec("Work Scope");
  push(polishedOrMissing(t.activity));
  sec("Key Hazards");
  push(polishedOrMissing(t.hazards));
  sec("Required Controls");
  push(polishedOrMissing(t.controls));
  sec("Required PPE");
  push(polishedOrMissing(t.ppe));
  sec("Stop-Work Triggers");
  push(polishedOrMissing(t.stopWork));
  sec("Crew Discussion Points");
  push(polishedOrMissing(t.discussion));
  sec("Lessons Learned");
  push(polishedOrMissing(t.lessons));
  sec("Notes");
  push(polishedOrMissing(state.notes?.roughNotes));
  sec("Attendee Sign-Off Placeholder");
  push(`Department / Crew: ${orMissing(h.department)}`);
  push(`Presenter / Supervisor: ${orMissing(h.supervisor)}`);
  push(`Attendees: ${orMissing(t.attendees)}`);
  push("Signatures recorded outside this tool.");
  return lines.join("\n");
}
export const toolboxChecklist = (s) => {
  const out = [];
  const h = s.header || {}, t = s.topic || {};
  if (!h.reportDate) out.push("Date missing");
  if (!h.rigName) out.push("Rig / site missing");
  if (!h.supervisor) out.push("Supervisor / presenter missing");
  if (!t.topic) out.push("Topic missing");
  if (!t.hazards) out.push("Main hazards missing");
  if (!t.controls) out.push("Controls missing");
  return out;
};
export const TOOLBOX_QUICK_PHRASES = [
  "No incidents reported.",
  "Maintain line-of-fire awareness.",
  "Use correct PPE.",
  "Confirm permits before starting.",
  "Stop work if conditions change.",
  "Watch for dropped objects.",
  "Verify isolation before work.",
  "Keep hands clear of pinch points.",
  "Maintain good housekeeping.",
  "Report unsafe conditions immediately.",
];

// ====== Pre-Tour Briefing =================================================
export function buildPreTour(state) {
  const h = state.header || {}, o = state.ops || {}, r = state.risks || {}, e = state.equipment || {};
  const lines = []; const push = (s) => lines.push(s);
  const sec = (l) => lines.push(`\n=== ${l.toUpperCase()} ===\n`);
  sec("Pre-Tour Briefing");
  push(`${orMissing(h.reportDate)} · ${orMissing(h.rigName)} · ${orMissing(h.wellName)} · Tour: ${orMissing(h.tour)}`);
  sec("Current Operation");
  push(`${orMissing(o.currentOp)} — Depth / Status: ${orMissing(o.currentDepth)}`);
  sec("Planned Tour Activities");
  push(polishedOrMissing(o.plannedOps));
  sec("Key Risks");
  push(polishedOrMissing(r.mainHazards));
  sec("Controls");
  push(polishedOrMissing(r.controls));
  sec("SIMOPS / Permit Concerns");
  push(`SIMOPS: ${polishedOrMissing(r.simops)}`);
  push(`Permits Active: ${polishedOrMissing(r.permits)}`);
  sec("Equipment Watch Items");
  push(polishedOrMissing(e.watchItems));
  sec("Weather / Marine Concerns");
  push(polishedOrMissing(r.weather));
  sec("Communication Plan");
  push(polishedOrMissing(o.commsPlan));
  sec("Stop-Work Triggers");
  push(polishedOrMissing(r.stopWork));
  sec("Emergency Response Reminder");
  push("Follow rig-specific emergency response procedures and muster instructions. Confirm muster routes and PPE locations.");
  sec("Crew Changes");
  push(polishedOrMissing(o.crewChanges));
  sec("Safety Alerts");
  push(polishedOrMissing(r.safetyAlerts));
  sec("Supervisor Notes");
  push(polishedOrMissing(state.notes?.roughNotes));
  push(`Supervisor / Toolpusher: ${orMissing(h.supervisor)}`);
  return lines.join("\n");
}
export const preTourChecklist = (s) => {
  const out = []; const h = s.header || {}, o = s.ops || {};
  if (!h.reportDate) out.push("Date missing");
  if (!h.rigName) out.push("Rig / site missing");
  if (!h.tour) out.push("Tour / shift missing");
  if (!o.currentOp) out.push("Current operation missing");
  if (!o.plannedOps) out.push("Planned operations missing");
  if (!h.supervisor) out.push("Supervisor / toolpusher missing");
  return out;
};
export const PRE_TOUR_QUICK_PHRASES = [
  "No NPT during last tour.",
  "Operations continued as planned.",
  "Weather standby — monitor sea state.",
  "Active permits to be confirmed at briefing.",
  "SIMOPS activities ongoing — coordinate with deck.",
  "Maintain communications on assigned channels.",
  "Stop work if conditions deteriorate.",
  "Muster routes and PPE locations confirmed.",
];

// ====== Pre-Job Briefing ==================================================
export function buildPreJob(state) {
  const h = state.header || {}, j = state.job || {}, r = state.risks || {}, p = state.permits || {};
  const lines = []; const push = (s) => lines.push(s);
  const sec = (l) => lines.push(`\n=== ${l.toUpperCase()} ===\n`);
  sec("Pre-Job Briefing");
  push(`${orMissing(h.reportDate)} · ${orMissing(h.rigName)} · Job: ${orMissing(j.job)}`);
  push(`Location: ${orMissing(j.location)}  ·  Supervisor: ${orMissing(h.supervisor)}`);
  push(`Crew Involved: ${orMissing(j.crew)}`);
  sec("Job Overview");
  push(polishedOrMissing(j.overview));
  sec("Step-by-Step Job Plan");
  push(polishedOrMissing(j.steps));
  sec("Tools and Equipment");
  push(polishedOrMissing(j.tools));
  sec("Hazards and Controls");
  push(`Hazards: ${polishedOrMissing(r.hazards)}`);
  push(`Controls: ${polishedOrMissing(r.controls)}`);
  sec("Required PPE");
  push(polishedOrMissing(r.ppe));
  sec("Permit / Isolation Requirements");
  push(`Permits Required: ${polishedOrMissing(p.permits)}`);
  push(`Isolation Requirements: ${polishedOrMissing(p.isolation)}`);
  sec("Lifting / Energy Control Notes");
  push(`Lifting: ${polishedOrMissing(p.lifting)}`);
  push(`Energy Sources: ${polishedOrMissing(p.energy)}`);
  sec("Communication Plan");
  push(polishedOrMissing(j.commsPlan));
  sec("Emergency Response");
  push(polishedOrMissing(j.emergency));
  sec("Stop-Work Conditions");
  push(polishedOrMissing(r.stopWork));
  sec("Supervisor Review Section");
  push(polishedOrMissing(state.notes?.roughNotes));
  push("Supervisor sign-off recorded outside this tool.");
  return lines.join("\n");
}
export const preJobChecklist = (s) => {
  const out = []; const h = s.header || {}, j = s.job || {}, r = s.risks || {};
  if (!h.reportDate) out.push("Date missing");
  if (!h.rigName) out.push("Rig / site missing");
  if (!h.supervisor) out.push("Supervisor missing");
  if (!j.job) out.push("Job / task missing");
  if (!j.crew) out.push("Crew involved missing");
  if (!r.hazards) out.push("Main hazards missing");
  if (!r.controls) out.push("Controls missing");
  if (!r.ppe) out.push("Required PPE missing");
  return out;
};
export const PRE_JOB_QUICK_PHRASES = [
  "Confirm permits to work prior to start.",
  "Verify isolation in place.",
  "Confirm rigging certified and in date.",
  "PPE confirmed for all crew.",
  "Stop work if conditions change.",
  "Communicate any deviation to supervisor.",
  "Maintain housekeeping at work site.",
];

// ====== JSA / Job Safety Analysis =========================================
export function buildJsa(state) {
  const h = state.header || {}, j = state.job || {}, table = Array.isArray(state.table) ? state.table : [];
  const env = state.environment || {}, perm = state.permits || {};
  const lines = []; const push = (s) => lines.push(s);
  const sec = (l) => lines.push(`\n=== ${l.toUpperCase()} ===\n`);
  sec("Job Task");
  push(`${orMissing(j.job)} — ${orMissing(j.location)}`);
  push(`${orMissing(h.rigName)} · Prepared by: ${orMissing(h.preparedBy)} · Crew: ${orMissing(j.crew)}`);
  sec("Scope of Work");
  push(polishedOrMissing(j.scope));
  sec("JSA Table");
  if (table.length === 0) {
    push(MISSING);
  } else {
    table.forEach((r, i) => {
      push(`${String(i + 1).padStart(2, "0")}. Step: ${orMissing(r.step)}`);
      push(`    Hazard: ${orMissing(r.hazard)}`);
      push(`    Control: ${orMissing(r.control)}`);
      push(`    Responsible: ${orMissing(r.responsible)}`);
      push(`    Residual Risk (user-entered, not calculated): ${orMissing(r.residual)}`);
    });
  }
  sec("PPE Requirements");
  push(polishedOrMissing(state.ppe?.text));
  sec("Permit / Isolation Requirements");
  push(`Permits Required: ${polishedOrMissing(perm.permits)}`);
  push(`Isolation Requirements: ${polishedOrMissing(perm.isolation)}`);
  sec("Environmental Controls");
  push(polishedOrMissing(env.controls));
  sec("Stop-Work Triggers");
  push(polishedOrMissing(state.stopWork?.text));
  sec("Approval / Sign-Off Placeholder");
  push(`Responsible Person: ${orMissing(state.signoff?.responsible)}`);
  push(`Supervisor Review: ${orMissing(state.signoff?.supervisor)}`);
  push("Approval signatures recorded outside this tool.");
  sec("Draft JSA Warning");
  push("Draft JSA support only. This tool does not replace site-specific hazard assessment, permit-to-work requirements, competent HSE review, supervisor approval, or company risk-management procedures.");
  return lines.join("\n");
}
export const jsaChecklist = (s) => {
  const out = []; const h = s.header || {}, j = s.job || {};
  if (!h.reportDate) out.push("Date missing");
  if (!h.rigName) out.push("Rig / site missing");
  if (!h.preparedBy) out.push("Prepared-By missing");
  if (!j.job) out.push("Job / task missing");
  if (!j.scope) out.push("Scope of work missing");
  const t = Array.isArray(s.table) ? s.table : [];
  if (t.length === 0) out.push("JSA table empty — add at least one step");
  if (!s.ppe?.text) out.push("PPE requirements missing");
  if (!s.stopWork?.text) out.push("Stop-work triggers missing");
  return out;
};
export const JSA_QUICK_PHRASES = [
  "Residual risk under review by supervisor.",
  "Confirm permits before starting.",
  "Stop work if hazard changes.",
  "PPE verified per company matrix.",
  "Environmental controls in place.",
  "Lifting plan certified and in date.",
];
